<template>
    <loader-gif v-if="isRequestSubscription" />

    <button type="button" @click="openContractMenu" :class="classes"
        :title="i18n.t('button.download', { title: 'contrat' })">
        <span class="d-flex align-items-center">
            <font-awesome-icon icon="file-pdf" size="lg" :class="showText ? 'me-2' : ''" />
            <span v-if="showText">{{ i18n.t('button.download', { title: 'contrat' }) }}</span>
        </span>
    </button>

</template>

<script lang="ts">
import { defineComponent, ref, toRefs } from "vue";
import Swal from "sweetalert2";
import i18n from "@/i18n/index";

/* Composables & States */
import { useGlobal } from "@/composables/global/useGlobal";
import { useModal } from "@/composables/modal/useModal";
import { usePermission } from "@/composables/auth/usePermission";
import { useContrat } from "@/composables/pdf/memberContract/useContrat";
import { useSubscription } from "@/composables/member/useSubscription";
import { currentClub, signatureImage, logoImage } from "@/shared/state";
import { updateContractStatusRequest } from "@/interfaces/subscriptionMember";

export default defineComponent({
    name: "member-contract",

    props: {
        member: {
            type: Object,
            default: null,
        },
        subscription: {
            type: Object,
            default: null,
        },
        // Prop jdida bach t-controler wach t-bayan text wla ghir icon (Default hia true)
        showText: {
            type: Boolean,
            default: true,
        },
        classes: {
            type: String,
            default: null,
        }
    },

    setup(props) {
        // --- 1. Reactive References & Props ---
        const { subscription, member, showText, classes } = toRefs(props);

       
        const APP_URL = process.env.VUE_APP_URL;

        // --- 2. Composables ---
        const { closeModal } = useModal();
        const { hasPermission } = usePermission();
        const { contratPDF } = useContrat();
        const { updateContractStatus, isRequestSubscription } = useSubscription();
        const { printOrSaveToPdf, getBase64Image, convertToBase64 } = useGlobal();

  

       

        const openContractMenu = async () => {
            const currentStatus = subscription?.value?.contract_status ?? 'pending';
            const currentStatusText = i18n.t("tables.subscription_member.contrat.status." + currentStatus);

            const result = await Swal.fire({
                title: "Gestion du Contrat",
                html: `<p class="text-muted mb-2">État actuel : <b>${currentStatusText}</b></p>`,
                showDenyButton: true,
                showCancelButton: true,
                confirmButtonText: '<i class="fa fa-download me-1"></i> Télécharger',
                denyButtonText: '<i class="fa fa-pen me-1"></i> Modifier l\'état',
                cancelButtonText: i18n.t("button.cancel"),
                confirmButtonColor: '#0d6efd',
                denyButtonColor: '#6c757d',
            });

            if (result.isConfirmed) {
                await exportContract();
            } else if (result.isDenied) {
                await openStatusModal();
            }
        };

        const exportContract = async () => {
            const currentStatus = subscription?.value?.contract_status ?? 'pending';
            const updateData: updateContractStatusRequest = {
                id: subscription?.value?.id ?? null,
                contract_status: currentStatus === 'pending' ? 'delivered' : currentStatus,
                contract_signed_at: subscription?.value?.contract_signed_at ?? null,
            };

            await updateContractStatus(updateData).then(async (res) => {
                if (subscription.value && subscription.value.contract_status === 'pending') {
                    subscription.value.contract_status = 'delivered';
                }
                const logoImageData = getBase64Image(logoImage.value);
                const signatureImageData = getBase64Image(signatureImage.value);
                const swalOption = {
                    title: i18n.t("message.confirmation.export.invoice.title"),
                    denyButtonText: i18n.t("message.confirmation.export.invoice.button.print"),
                    cancelButtonText: i18n.t("button.cancel"),
                    confirmButtonText: i18n.t("message.confirmation.export.invoice.button.pdf"),
                };

                const exportedType = await printOrSaveToPdf(swalOption);
                if (exportedType !== "cancel") {
                    contratPDF(
                        member.value,
                        subscription.value,
                        {
                            logoImageData,
                            logoNaturalWidth: logoImage?.value?.naturalWidth,
                            logoNaturalHeight: logoImage?.value?.naturalHeight,
                        },
                        {
                            signatureImageData : signatureImageData ?? null ,
                            signatureNaturalWidth: signatureImage?.value?.naturalWidth ?? null,
                            signatureNaturalHeight: signatureImage?.value?.naturalHeight ?? null,
                        },
                        exportedType
                    );
                }
            });
        };

        const updateContractStatusApi = async (newStatus: string, signedDate: string | null) => {
            const updateData: updateContractStatusRequest = {
                id: subscription?.value?.id ?? null,
                contract_status: newStatus,
                contract_signed_at: signedDate,
            };

            try {
                await updateContractStatus(updateData);

                if (subscription.value) {
                    subscription.value.contract_status = newStatus;
                    subscription.value.contract_signed_at = signedDate;
                }

                Swal.fire({
                    icon: 'success',
                    title: i18n.t("message.success") || "Modifié avec succès",
                    timer: 1500,
                    showConfirmButton: false,
                });
            } catch (error) {
                Swal.fire({
                    icon: 'error',
                    title: i18n.t("message.error") || "Erreur",
                });
            }
        };

        const openStatusModal = async () => {
            const activeStatus = subscription?.value?.contract_status ?? 'pending';
            const rawSignedDate = subscription?.value?.contract_signed_at;

            let formattedDate = new Date().toISOString().split('T')[0];
            if (rawSignedDate) {
                formattedDate = String(rawSignedDate).substring(0, 10);
            }

            const { value: formValues } = await Swal.fire({
                title: i18n.t("tables.subscription_member.contrat.contract_status"),
                html: `
                    <div class="form-group text-start mb-3">
                        <label class="form-label mb-1">${i18n.t("tables.subscription_member.contrat.contract_status")}</label>
                        <select id="swal-input-status" class="swal2-select w-100 m-0">
                            <option value="pending" ${activeStatus === 'pending' ? 'selected' : ''}>${i18n.t("tables.subscription_member.contrat.status.pending")}</option>
                            <option value="delivered" ${activeStatus === 'delivered' ? 'selected' : ''}>${i18n.t("tables.subscription_member.contrat.status.delivered")}</option>
                            <option value="signed" ${activeStatus === 'signed' ? 'selected' : ''}>${i18n.t("tables.subscription_member.contrat.status.signed")}</option>
                        </select>
                    </div>
                    <div class="form-group text-start" id="swal-date-container" style="display: none;">
                        <label class="form-label mb-1">${i18n.t("tables.subscription_member.contrat.contract_signed_at")} <span class="text-danger">*</span></label>
                        <input type="date" id="swal-input-date" class="swal2-input w-100 m-0" value="${formattedDate}">
                    </div>
                `,
                focusConfirm: false,
                showCancelButton: true,
                confirmButtonText: i18n.t("button.save") || "Enregistrer",
                cancelButtonText: i18n.t("button.cancel"),
                didOpen: () => {
                    const statusSelect = document.getElementById('swal-input-status') as HTMLSelectElement;
                    const dateContainer = document.getElementById('swal-date-container') as HTMLElement;

                    const toggleDateVisibility = () => {
                        if (statusSelect.value === 'signed') {
                            dateContainer.style.display = 'block';
                        } else {
                            dateContainer.style.display = 'none';
                        }
                    };

                    toggleDateVisibility();
                    statusSelect.addEventListener('change', toggleDateVisibility);
                },
                preConfirm: () => {
                    const status = (document.getElementById('swal-input-status') as HTMLSelectElement).value;
                    const dateInput = document.getElementById('swal-input-date') as HTMLInputElement;
                    const date = dateInput ? dateInput.value : '';

                    // Validation: Ila kan signed w date khawi, 3ti validation message
                    if (status === 'signed' && !date) {
                        Swal.showValidationMessage("La date de signature est obligatoire lorsque le contrat est signé.");
                        return false;
                    }

                    return {
                        status: status,
                        date: status === 'signed' ? date : null
                    };
                }
            });

            if (formValues) {
                await updateContractStatusApi(formValues.status, formValues.date);
            }
        };

        // --- 4. Return Statement --- 
        return {
            showText,
            classes,
            openContractMenu,
            exportContract,
            openStatusModal,
            i18n,
            APP_URL,
            closeModal,
            hasPermission,
            isRequestSubscription,
        };
    },
});
</script>

<style scoped>
.contract-management-panel {
    width: 100%;
}
</style>